import React from 'react';
import CollapsibleSection from './CollapsibleSection';
import { AlertTriangle, CheckCircle, Info, Lightbulb } from 'lucide-react';

const ValidationPanel = ({ edgeValidation, fullValidationResults, nodes, onJump, sectionId, onUndock }) => {
  const getNodeById = (nodeId) => nodes.find(n => n.id === nodeId);

  // Determine which validation results to use
  const validationResults = fullValidationResults || (edgeValidation ? { [edgeValidation.nodeId]: edgeValidation.validation } : {});
  const isEdgeSpecificValidation = edgeValidation && !fullValidationResults;

  const getIssuesByType = () => {
    const warnings = [];
    const infos = [];
    let validCount = 0;

    Object.entries(validationResults).forEach(([nodeId, validation]) => {
      const node = getNodeById(nodeId);
      // Survey-level issues (no Start node, unreachable nodes) have no node to
      // jump to — they still have to be shown, so they render as "This survey".
      const isSurveyLevel = !node;

      if (validation.isValid) {
        validCount++;
      } else {
        validation.issues.forEach(issue => {
          const issueWithNode = {
            ...issue,
            nodeId: isSurveyLevel ? null : nodeId,
            nodeName: isSurveyLevel ? 'This survey' : (node.data.text || node.data.id || nodeId)
          };

          if (issue.severity === 'warning' || issue.severity === 'error') {
            warnings.push(issueWithNode);
          } else {
            infos.push(issueWithNode);
          }
        });
      }
    });

    return { warnings, infos, validCount };
  };

  const { warnings, infos, validCount } = getIssuesByType();
  // Count only entries that belong to a real node, so the survey-level entry
  // doesn't inflate the "N of M questions" line.
  const totalQuestions = Object.keys(validationResults).filter(id => getNodeById(id)).length;

  const handleIssueClick = (nodeId) => {
    if (!nodeId) return;
    if (typeof onJump === 'function') onJump(nodeId);
    try { window.dispatchEvent(new CustomEvent('graph-center-node', { detail: { nodeId } })); } catch (_) {}
  };

  const renderIssueList = (issues, icon, color) => {
    if (issues.length === 0) return null;

    return (
      <div style={{ marginBottom: '15px' }}>
        {issues.map((issue, index) => (
          <div
            key={index}
            onClick={() => handleIssueClick(issue.nodeId)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '8px',
              marginBottom: '6px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: `1px solid ${color}20`,
              cursor: issue.nodeId ? 'pointer' : 'default'
            }}
          >
            <div style={{ color, marginTop: '2px', flexShrink: 0 }}>
              {icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '2px' }}>
                {issue.nodeName}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                {issue.message}
              </div>
              {issue.suggestion && (
                <div style={{
                  fontSize: '11px',
                  color: '#007bff',
                  fontStyle: 'italic'
                }}>
                  <Lightbulb size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {issue.suggestion}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (totalQuestions === 0) {
    return (
      <CollapsibleSection title="Flow Validation" defaultCollapsed={false} sectionId={sectionId} onUndock={onUndock}>
        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
          <Info size={32} style={{ marginBottom: '10px', opacity: 0.5 }} />
          <p>{isEdgeSpecificValidation ? 'Select an edge to see validation results' : 'Add some questions to see validation results'}</p>
        </div>
      </CollapsibleSection>
    );
  }

  const hasIssues = warnings.length > 0 || infos.length > 0;
  const titlePrefix = isEdgeSpecificValidation ? 'Edge Validation' : 'Flow Validation';

  return (
    <CollapsibleSection
      title={<>{titlePrefix} {hasIssues ? <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> : <CheckCircle size={14} style={{ verticalAlign: 'middle' }} />}</>}
      defaultCollapsed={!hasIssues}
      sectionId={sectionId}
      onUndock={onUndock}
    >
      <div style={{ marginBottom: '15px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          color: warnings.length > 0 ? '#856404' : '#155724'
        }}>
          {warnings.length > 0 ? (
            <AlertTriangle size={16} />
          ) : (
            <CheckCircle size={16} />
          )}
          <span>
            {isEdgeSpecificValidation
              ? `Validation for selected edge (${totalQuestions} question${totalQuestions !== 1 ? 's' : ''})`
              : `${validCount} of ${totalQuestions} questions have complete response coverage`
            }
          </span>
        </div>
      </div>

      {warnings.length > 0 && (
        <>
          <h4 style={{ margin: '10px 0 8px 0', fontSize: '13px', color: '#856404' }}>
            <AlertTriangle size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Problems Found
          </h4>
          {renderIssueList(warnings, <AlertTriangle size={14} />, '#ffc107')}
        </>
      )}

      {infos.length > 0 && (
        <>
          <h4 style={{ margin: '10px 0 8px 0', fontSize: '13px', color: '#0066cc' }}>
            <Info size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Suggestions
          </h4>
          {renderIssueList(infos, <Info size={14} />, '#007bff')}
        </>
      )}

      {!hasIssues && (
        <div style={{
          textAlign: 'center',
          color: '#155724',
          padding: '10px',
          backgroundColor: '#d4edda',
          borderRadius: '4px',
          border: '1px solid #c3e6cb'
        }}>
          <CheckCircle size={20} style={{ marginBottom: '5px' }} />
          <div style={{ fontSize: '13px' }}>
            All questions have proper response coverage!
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default ValidationPanel; 